import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Mail } from "lucide-react";
import { subscribeNewsletter } from "../../lib/api";
import { track, events } from "../../lib/analytics";
import { getFunnelCopy } from "../../lib/categoryCopy";

/**
 * Iter-165 — "Stay Connected (Optional)" newsletter capture.
 *
 * Replaces the iter-153 accordion that promised rate-monitoring
 * features CarSavr doesn't yet operate. That was a trust violation +
 * partner-approval red flag (those promises require infrastructure we
 * don't have).
 *
 * New copy is truthful: "Get occasional savings tips, new tools, and
 * ownership insights from CarSavr. No spam." No accordion, no promise
 * of monitoring — just a simple always-visible email capture sitting
 * below the primary "Compare Options" CTA.
 *
 * Subscribes via the idempotent /api/newsletter Resend pipeline. Reports
 * EMAIL_CAPTURE_SUBMITTED with `source` discriminating between the
 * homepage form ("quote_success_card") and modal ("intake_success_card")
 * surfaces.
 */
export default function StayConnectedForm({ category, source }) {
  const [email, setEmail] = useState("");
  // idle | saving | captured | error
  const [state, setState] = useState("idle");

  const copy = getFunnelCopy(category);
  const headline = copy.email_capture_headline;
  const description = copy.email_capture_description;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setState("saving");
    try {
      await subscribeNewsletter(trimmed);
      setState("captured");
      track(events.EMAIL_CAPTURE_SUBMITTED, {
        category,
        source,
        surface: "stay_connected_form",
      });
      toast.success("You're on the list — thanks!");
    } catch {
      setState("error");
      toast.error("Couldn't save — try again later.");
    }
  };

  if (state === "captured") {
    return (
      <div
        data-testid="stay-connected-captured"
        className="mt-5 flex items-start gap-2.5 bg-emerald-50 border border-emerald-200 px-4 py-3"
      >
        <CheckCircle2
          className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5"
          strokeWidth={2.5}
        />
        <div className="min-w-0">
          <p className="text-xs font-bold text-emerald-900 leading-tight">
            You&apos;re on the list — {email}.
          </p>
          <p className="text-[11px] text-emerald-800 mt-0.5 leading-snug">
            We&apos;ll only email when there&apos;s something genuinely
            useful to share.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="stay-connected-form"
      className="mt-5 bg-[#FAFAFA] border border-slate-200 p-4 sm:p-5 space-y-3"
    >
      <div className="flex items-start gap-2.5">
        <Mail
          className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5"
          strokeWidth={2.5}
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p
            data-testid="stay-connected-headline"
            className="text-sm font-bold text-slate-950 leading-tight"
          >
            {headline}
          </p>
          <p
            data-testid="stay-connected-description"
            className="text-[12px] text-slate-600 leading-relaxed mt-1"
          >
            {description}
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2.5">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email Address"
          autoComplete="email"
          disabled={state === "saving"}
          aria-label="Email address"
          data-testid="stay-connected-input"
          // Iter-170 — Bumped to h-12 + explicit w-full so the mobile
          // stack renders the input at full width + a comfortable touch
          // target size matching the surrounding modal field heights.
          // Pre-iter-170 the input was h-10 + flex-1 only, which made
          // it visibly skinnier than the modal form fields above it.
          className="w-full sm:flex-1 h-12 px-3.5 border border-slate-300 focus:border-slate-950 focus:outline-none text-sm bg-white"
        />
        <button
          type="submit"
          disabled={state === "saving" || !email.trim()}
          data-testid="stay-connected-submit"
          // Iter-170 — Bumped to h-12 + w-full on mobile so the Save
          // Email button visually matches the input above and fills
          // the column edge-to-edge on small screens.
          className="press inline-flex items-center justify-center bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold w-full sm:w-auto h-12 px-5 text-xs whitespace-nowrap uppercase tracking-[0.14em]"
        >
          {state === "saving" ? "Saving…" : "Save Email"}
        </button>
      </div>
    </form>
  );
}
